import React, { useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';
import { styles } from './Swatches.css';
import Image from '../../../common/Image';

interface SwatchesProps {
  name: string;
  variants: Array<{
    imageUrl: string;
    name: string;
    current: boolean;
    handle: string;
    sku: string;
  }>;
  switchProduct: (handle, name) => void;
  itemGapPx?: number;
}

const Swatches = ({ name, variants, switchProduct, itemGapPx = 10 }: SwatchesProps): JSX.Element => {
  const isColourOption = name === 'Colour';
  const sliderRef = useRef<any>();
  const hoverTextRef = useRef<any>();
  const [hasSlider, setHasSlider] = useState(variants.length > 4);
  const variantsContainerRef = useRef<any>();
  const formattedIdName = `swatch-variants-${name.toLowerCase().replace(/\s/g, '-')}`;

  // handle metafields.online_product.value.Family.Options['Color'].Children not having the Value property set
  const currentText = variants?.find(variant => !!variant.current)
    ? variants?.find(variant => !!variant.current)[name]?.value
    : '';
  const initialSlide = variants?.findIndex(variant => !!variant.current) || 0;

  const updateHoverText = (text: string) => {
    if (hoverTextRef.current) {
      hoverTextRef.current.innerHTML = text;
    }
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      if (hoverTextRef.current) {
        hoverTextRef.current.innerHTML = currentText;
      }

      // Checking if the total width of all options is bigger than the width of the container, if yes, we initialize the slider
      if (variantsContainerRef.current) {
        setTimeout(() => {
          const sliderElements = variantsContainerRef.current.querySelectorAll('.flickity-slider a');
          const sliderElementCount = sliderElements.length;
          if (sliderElements && sliderElementCount) {
            const containerWidth = variantsContainerRef.current.offsetWidth;
            let totalWidth = 0;
            sliderElements.forEach((element, index) => {
              totalWidth += element.offsetWidth + (index + 1 !== sliderElementCount ? itemGapPx : 0);
            });
            if (totalWidth > containerWidth) {
              setHasSlider(true);
            } else {
              setHasSlider(false);
            }
          }
        }, 300);
      }
    }

    return () => {
      mounted = false;
    };
  }, [variants]);

  return (
    <div className={styles.variant} id={formattedIdName}>
      <div className={styles.title}>{name}</div>
      <div
        className={styles.container({ isColourOption })}
        ref={variantsContainerRef}
        onMouseOut={() => updateHoverText(currentText)}
        data-slider-enabled={hasSlider}
      >
        <Flickity
          key={Date.now()}
          elementType={'div'}
          flickityRef={ref => (sliderRef.current = ref)}
          disableImagesLoaded={false}
          options={{
            groupCells: true,
            prevNextButtons: hasSlider,
            pageDots: false,
            freeScroll: true,
            wrapAround: false,
            contain: true,
            draggable: hasSlider,
            percentPosition: false,
            initialIndex: Math.floor(initialSlide / 4),
            cellAlign: 'left',
          }}
        >
          {variants.map(variant => (
            <a
              key={`${variant.sku}-${variant.handle}`}
              href={`/products/${variant.handle}`}
              onClick={e => {
                if (e.metaKey || e.ctrlKey) return; // Allow opening in new tab with cmd/ctrl + click
                e.preventDefault();
                switchProduct(variant?.handle, name);
              }}
              onMouseOver={() => updateHoverText(variant[name]?.value)}
              className={!!variant.current ? 'current' : ''}
            >
              <Image
                src={variant.imageUrl}
                alt={variant[name]?.value}
                width={60}
                srcSetWidths={[60, 120, 180]}
                sizes="60px"
                lazyload={false}
              />
            </a>
          ))}
        </Flickity>
      </div>
      <div ref={hoverTextRef} className={styles.highlighted} />
    </div>
  );
};

export default Swatches;
